/**
 * 1.8.4 §3 (D-1.8.4-3) — a tapped line's small window: what it is, when, who (as the role may see), Call where the
 * role may call (the 1.8.3 rule: no number printed — CallButton writes only the word) and Open in [program] to go
 * where it can be changed. Today itself changes nothing: no Cancel, no Move here.
 */
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { CallButton } from '@/components/Contact';
import { formatDayKey } from '@/lib/dates';
import { langOf } from '@/features/calendar/lineWords';
import { useOpenIn } from '@/app/program';
import { categoryWord, itemTime, openWords, type DayItem } from './words';

export interface LineWindowProps {
  item: DayItem | null;
  dayKey: string;
  /** The role may call from Today (the administrator, a co-administrator). */
  canCall: boolean;
  onClose: () => void;
  returnFocusTo: RefObject<HTMLElement | null>;
}

export function LineWindow({ item, dayKey, canCall, onClose, returnFocusTo }: LineWindowProps) {
  const { t, i18n } = useTranslation();
  const lang = langOf(i18n.language);
  const openIn = useOpenIn();
  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      {item ? (
        <SheetContent title={`${categoryWord(item.category, t)} · ${itemTime(item, t, lang)}`} returnFocusTo={returnFocusTo} data-testid="t184-window" data-cat={item.category}>
          <dl className="kv t184-window-facts">
            <dt>{t('today184.window.what')}</dt>
            <dd>
              <span className="cal182-tag" data-cat={item.category}>
                <span className="cal182-dot" aria-hidden="true" />
                {categoryWord(item.category, t)}
              </span>{' '}
              {item.what}
            </dd>
            <dt>{t('today184.window.when')}</dt>
            <dd>
              {formatDayKey(dayKey, i18n.language)} · {itemTime(item, t, lang)}
            </dd>
            {item.who ? (
              <>
                <dt>{t('today184.window.who')}</dt>
                <dd>{item.who}</dd>
              </>
            ) : null}
          </dl>
          <div className="t184-window-acts">
            {canCall && item.phone ? <CallButton phone={item.phone} name={item.callName || item.who || ''} /> : null}
            {item.href ? (
              <Button
                onClick={() => {
                  const href = item.href!;
                  onClose();
                  openIn(href, item.openIn);
                }}
                data-testid="t184-window-open"
              >
                {openWords(item, t)}
              </Button>
            ) : null}
          </div>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}
